// View + cursor state and the per-view date math (SPEC-CalendarToolbar.md §4).
//
// Week starts Sunday (`getWeekStart = date − getDay()`), not locale-derived.
// The pure helpers (`rangeForView`, `shiftCursor`) are exported so the math is
// unit-tested without rendering.

import { useCallback, useMemo, useState } from 'react'
import { addDays, addMonths, startOfDay, startOfWeek } from 'date-fns'
import type { CalendarView } from '../typings'
import {
  MONTH_MATRIX_DAYS,
  monthMatrix,
} from '../components/CalendarGrid/monthMatrix'

// Sunday-start week.
const WEEK_STARTS_ON = 0

export type CalendarRange = {
  /** Inclusive start of the range (midnight). */
  start: Date
  /** EXCLUSIVE end — the start of the day after the range's last day. */
  end: Date
  /** INCLUSIVE last day (midnight) — convenient for headings. */
  endInclusive: Date
}

/** The `[start, end)` range covering `cursor` for the given view. */
export function rangeForView(cursor: Date, view: CalendarView): CalendarRange {
  switch (view) {
    case 'month': {
      // The whole visible 6-week matrix, spill days included, so adjacent-month
      // cells show their events. It exceeds the endpoint's 31-day cap; the fetch
      // layer splits it into accepted windows.
      const start = monthMatrix(cursor)[0]
      const end = addDays(start, MONTH_MATRIX_DAYS)
      return { start, end, endInclusive: addDays(end, -1) }
    }
    case 'day': {
      const start = startOfDay(cursor)
      const end = addDays(start, 1)
      return { start, end, endInclusive: start }
    }
    case 'week':
    default: {
      const start = startOfWeek(cursor, { weekStartsOn: WEEK_STARTS_ON })
      const end = addDays(start, 7)
      return { start, end, endInclusive: addDays(start, 6) }
    }
  }
}

/** Shift the cursor by one view-unit: ±1 month / ±7 days / ±1 day. */
export function shiftCursor(
  cursor: Date,
  view: CalendarView,
  direction: 1 | -1,
): Date {
  switch (view) {
    case 'month':
      return addMonths(cursor, direction)
    case 'day':
      return addDays(cursor, direction)
    case 'week':
    default:
      return addDays(cursor, direction * 7)
  }
}

export type UseCalendarNavigationResult = {
  view: CalendarView
  cursor: Date
  range: CalendarRange
  setView: (view: CalendarView) => void
  goToday: () => void
  goPrev: () => void
  goNext: () => void
  /** Open the day view on `date` (e.g. from a "+N more" affordance). */
  goToDay: (date: Date) => void
}

export function useCalendarNavigation(
  initialView: CalendarView = 'week',
  initialCursor: Date = new Date(),
): UseCalendarNavigationResult {
  const [view, setView] = useState<CalendarView>(initialView)
  const [cursor, setCursor] = useState<Date>(() => startOfDay(initialCursor))

  const goToday = useCallback(() => setCursor(startOfDay(new Date())), [])
  const goPrev = useCallback(
    () => setCursor((c) => shiftCursor(c, view, -1)),
    [view],
  )
  const goNext = useCallback(
    () => setCursor((c) => shiftCursor(c, view, 1)),
    [view],
  )

  const goToDay = useCallback((date: Date) => {
    setCursor(startOfDay(date))
    setView('day')
  }, [])

  const range = useMemo(() => rangeForView(cursor, view), [cursor, view])

  return { view, cursor, range, setView, goToday, goPrev, goNext, goToDay }
}
